// Shared typed helper for the "patch a few keys of this route's own search
// params" navigate pattern (switch a tab, change a date range, etc). Wraps
// `navigate({ search: prev => ({ ...prev, ...patch }) })` once, with the single
// unavoidable type assertion contained here instead of scattered across call
// sites as `@ts-expect-error` — TanStack Router's `ParamsReducerFn` generics
// don't narrow correctly through a generic wrapper like this one, but the
// runtime behavior (merge onto the current location's search) is exactly what
// the router does natively for a same-route `search: prev => next` navigate.
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

export function useSearchPatch<TSearch extends Record<string, unknown>>(defaultOptions?: {
  replace?: boolean;
}) {
  const navigate = useNavigate();
  return useCallback(
    (patch: Partial<TSearch>, options?: { replace?: boolean }) => {
      void navigate({
        search: (prev: TSearch) => ({ ...prev, ...patch }),
        replace: options?.replace ?? defaultOptions?.replace,
      } as never);
    },
    [navigate, defaultOptions?.replace],
  );
}
